import { type UseQueryResult } from '@tanstack/react-query';
import { type NonNullableQuiz, type Quiz } from '../defs/quiz';
import {
  ROUTE_QUIZ_HELLO,
  ROUTE_QUIZ_INTRO,
  type AllQuizRoutes,
} from '../defs/routes';
import { getRedirectPath } from '../navigation/navigation';
import { useQuizRoutes } from './useQuizRoutes';

function isValidNavigationString<T>(routes: readonly T[], loc: string) {
  if (routes.includes(loc as T)) {
    return loc as T;
  }
  return undefined;
}

export interface NavigationNode {
  prev: AllQuizRoutes | null;
  path: AllQuizRoutes;
  next: AllQuizRoutes | null;
}

type Nullable<T> = { [K in keyof T]: T[K] | null };
type NavigationMap = Record<AllQuizRoutes, NavigationNode>;

/**
 * Note: For the post-quiz redirects, see use-funnel-redirects.ts
 */
export function buildNavigationMap(routes: readonly AllQuizRoutes[]) {
  return routes.reduce((seed, nav, index) => {
    const prev = index === 0 ? null : routes[index - 1];
    const next = routes[index + 1] === undefined ? null : routes[index + 1];
    seed[nav] = {
      prev,
      path: nav,
      next,
    };
    return seed;
  }, {} as NavigationMap);
}

export function getNavigationState(
  routes: readonly AllQuizRoutes[],
  loc: AllQuizRoutes
): NavigationNode {
  if (!isValidNavigationString(routes, loc)) {
    throw new Error(`"${loc}" is not a valid url string for the Quiz`);
  }

  const map = buildNavigationMap(routes);
  const { next, prev } = map[loc];

  return {
    next,
    path: loc,
    prev: prev === ROUTE_QUIZ_INTRO ? null : prev,
  };
}

type Params = {
  loc: string;
  searchParams?: string;
  query: Pick<UseQueryResult<Quiz | null>, 'data' | 'isFetching'>;
  quiz: Nullable<NonNullableQuiz> | undefined;
};

type QuizNavigationState =
  | {
      redirect: AllQuizRoutes;
      navigation?: never;
      routes: readonly AllQuizRoutes[];
    }
  | {
      routes: readonly AllQuizRoutes[];
      redirect?: never;
      navigation: NavigationNode;
    };

export function useQuizNavigation({
  quiz,
  loc,
  searchParams,
  query,
}: Params): QuizNavigationState | undefined {
  const quizRoutes = useQuizRoutes(quiz);

  if (quizRoutes === undefined) {
    return;
  }

  const redirectPath = getRedirectPath({
    loc,
    searchParams,
    query: query,
    quizRoutes,
  });

  if (redirectPath !== null) {
    return {
      routes: quizRoutes,
      redirect: redirectPath,
    };
  }

  const validated = isValidNavigationString(quizRoutes, loc);

  // if the user comes on an invalid url, redirect
  if (validated === undefined) {
    return {
      routes: quizRoutes,
      redirect: ROUTE_QUIZ_HELLO,
    };
  }

  const navigationState = getNavigationState(quizRoutes, validated);

  return {
    routes: quizRoutes,
    navigation: navigationState,
  };
}
